import {
  ListProOpenJobCountsRequest,
  GetJobTotalsRequest,
  GetJobTotalStatusAggregationsRequest,
  GetJobTotalPriceAggregationsRequest,
} from '@taxfyle/api-internal/internal/job_stats_pb'
import { Interval } from '@taxfyle/api-internal/shared/types/interval_pb'
import { Id } from '@/infra/id'
import { ISOToTimestamp } from '@/utils/grpc-util'
import { type JobsApi } from '@/features/jobs/api/job-api'
import { mapProOpenJobCountProtoToProOpenJobCount } from './pro-open-job-count-mappers'
import {
  mapJobTotalPriceAggregationsProtoToJobsTotalPricing,
  mapJobTotalsProtoToJobTotals,
  mapJobTotalStatusAggregationsProtoToJobTotalStatusAggregations,
} from './job-totals-mappers'

/**
 * Options for {@link JobStatsStore.fetchJobsTotalPricing}.
 */
export interface FetchJobsTotalPricingOptions {
  workspaceId: Id
  contractStartDate: Date | null
  contractEndDate: Date | null
}

/**
 * Options for {@link JobStatsStore.fetchJobTotalStatusAggregations}.
 */
export interface FetchJobTotalStatusAggregationsOptions {
  userPublicId: Id
  workspaceId: Id
}

/**
 * Fetches job counts and totals for dashboards.
 */
export class JobStatsStore {
  constructor(private readonly _api: JobsApi) {
    this.fetchProOpenJobCounts = this.fetchProOpenJobCounts.bind(this)
    this.fetchJobTotals = this.fetchJobTotals.bind(this)
    this.fetchJobTotalStatusAggregations =
      this.fetchJobTotalStatusAggregations.bind(this)
    this.fetchJobsTotalPricing = this.fetchJobsTotalPricing.bind(this)
  }

  /**
   * Fetches how many open jobs a pro has in each workspace.
   */
  async fetchProOpenJobCounts(userPublicId: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()

    const result = await this._api.jobStats.listProOpenJobCounts(
      new ListProOpenJobCountsRequest().setUserId(Id.assert(userPublicId)),
    )
    signal?.throwIfAborted()

    return result
      .getProOpenJobCountsList()
      .map(mapProOpenJobCountProtoToProOpenJobCount)
  }

  /**
   * Fetches how many jobs a user has, and how many are active.
   */
  async fetchJobTotals(userPublicId: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()

    const response = await this._api.jobStats.getJobTotals(
      new GetJobTotalsRequest().setUserId(Id.assert(userPublicId)),
    )
    signal?.throwIfAborted()

    return mapJobTotalsProtoToJobTotals(response)
  }

  /**
   * Fetches how many of a user's jobs in a workspace are in each status.
   */
  async fetchJobTotalStatusAggregations(
    { userPublicId, workspaceId }: FetchJobTotalStatusAggregationsOptions,
    signal?: AbortSignal,
  ) {
    signal?.throwIfAborted()

    const response = await this._api.jobStats.getJobTotalStatusAggregations(
      new GetJobTotalStatusAggregationsRequest()
        .setUserId(Id.assert(userPublicId))
        .setWorkspaceId(Id.assert(workspaceId)),
    )
    signal?.throwIfAborted()

    return mapJobTotalStatusAggregationsProtoToJobTotalStatusAggregations(
      response,
    )
  }

  /**
   * Gets the total sum for submitted and closed jobs pricing.
   */
  async fetchJobsTotalPricing(
    {
      workspaceId,
      contractStartDate,
      contractEndDate,
    }: FetchJobsTotalPricingOptions,
    signal?: AbortSignal,
  ) {
    signal?.throwIfAborted()

    const request = new GetJobTotalPriceAggregationsRequest().setWorkspaceId(
      Id.assert(workspaceId),
    )

    if (contractStartDate && contractEndDate) {
      request.setDateRange(
        new Interval()
          .setStartTime(ISOToTimestamp(contractStartDate.toISOString()))
          .setEndTime(ISOToTimestamp(contractEndDate.toISOString())),
      )
    }

    const response =
      await this._api.jobStats.getJobTotalPriceAggregations(request)
    signal?.throwIfAborted()

    return mapJobTotalPriceAggregationsProtoToJobsTotalPricing(response)
  }
}
